import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Users } from 'lucide-react';
import Navbar from '@/components/layout/Navbar';
import Footer from '@/components/layout/Footer';
import SEO from '@/components/shared/SEO';
import { getClients } from '@/lib/api';

export default function ClientsPage() {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    getClients()
      .then(res => setClients(res.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <SEO title="Our Clients" description="Trusted by leading hotels, restaurants, and hospitality brands across India." />
      <Navbar />

      {/* Hero */}
      <section className="pt-28 pb-16 bg-gradient-to-b from-[#14532D] to-[#1a6b3a]" data-testid="clients-hero">
        <div className="max-w-7xl mx-auto px-6 md:px-12 text-center">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
            <span className="inline-flex items-center gap-2 px-4 py-1.5 bg-white/10 rounded-full text-[#D4AF37] text-xs font-semibold uppercase tracking-widest mb-6">
              <Users className="w-3.5 h-3.5" /> Our Valued Partners
            </span>
            <h1 className="text-4xl sm:text-5xl font-bold text-white mb-4" style={{ fontFamily: 'Manrope' }}>
              Clients Who Trust Us
            </h1>
            <p className="text-white/60 max-w-lg mx-auto text-sm md:text-base">
              Proudly serving prestigious hotel chains, airlines, and hospitality brands across India since 2010.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Client Logos Grid */}
      <section className="py-16 md:py-24" data-testid="clients-grid-section">
        <div className="max-w-6xl mx-auto px-6 md:px-12">
          {loading ? (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-5">
              {Array(15).fill(0).map((_, i) => (
                <div key={i} className="aspect-[3/2] rounded-xl bg-gray-100 animate-pulse" />
              ))}
            </div>
          ) : clients.length > 0 ? (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-6" data-testid="clients-grid">
              {clients.map((client, idx) => (
                <motion.div
                  key={client.id}
                  initial={{ opacity: 0, scale: 0.9 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true, margin: '-20px' }}
                  transition={{ duration: 0.3, delay: idx * 0.03 }}
                  className="group bg-white border border-gray-100 rounded-xl p-6 md:p-8 flex flex-col items-center justify-center aspect-[4/3] hover:shadow-lg hover:border-[#D4AF37]/30 transition-all duration-300"
                  data-testid={`client-logo-${client.id}`}
                >
                  <img
                    src={client.logo_url}
                    alt={client.name}
                    className="max-h-24 md:max-h-28 w-auto max-w-[85%] object-contain opacity-75 group-hover:opacity-100 transition-opacity duration-300"
                    loading="lazy"
                  />
                  {client.name && (
                    <p className="text-xs text-gray-400 mt-3 font-medium text-center">
                      {client.name}
                    </p>
                  )}
                </motion.div>
              ))}
            </div>
          ) : (
            <p className="text-center text-muted-foreground">No clients added yet.</p>
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
}
